import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, keyframes } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { PollItemHighlight } from './types';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(120,105,223,0); }
  50% { box-shadow: 0 0 18px rgba(120,105,223,0.18), 0 0 32px rgba(102,126,234,0.10); }
`;

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

interface Props {
  positive: PollItemHighlight[];
  negative: PollItemHighlight[];
  loading: boolean;
}

const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 320,
    }}
  />
);

// ─── Sparkline SVG ────────────────────────────────────────────────────────
interface SparklineProps {
  data: number[];
  color: string;
  width?: number;
  height?: number;
}

const Sparkline: React.FC<SparklineProps> = ({ data, color, width = 100, height = 28 }) => {
  if (!data.length || data.every((v) => v === 0)) {
    return (
      <svg width={width} height={height}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="#e2e8f0"
          strokeWidth={1.5}
          strokeDasharray="2 3"
        />
      </svg>
    );
  }

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1 || 1);

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });

  const pathD = `M ${points.join(' L ')}`;
  const areaD = `${pathD} L ${width},${height} L 0,${height} Z`;

  return (
    <svg width={width} height={height}>
      <defs>
        <linearGradient id={`sparkline-grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.25} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <motion.path
        d={areaD}
        fill={`url(#sparkline-grad-${color.replace('#', '')})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: EASE_OUT }}
      />
      <motion.path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.8, ease: EASE_OUT }}
      />
    </svg>
  );
};

// ─── Trend direction from last 2 weeks ──
const trendDirection = (data: number[]): 'up' | 'down' | 'flat' => {
  if (data.length < 2) return 'flat';
  const recent = data.slice(-3).reduce((a, b) => a + b, 0);
  const older = data.slice(0, 3).reduce((a, b) => a + b, 0);
  if (recent > older * 1.2) return 'up';
  if (recent < older * 0.8) return 'down';
  return 'flat';
};

interface RowProps {
  item: PollItemHighlight;
  color: string;
  idx: number;
}

const TrendRow: React.FC<RowProps> = ({ item, color, idx }) => {
  const direction = useMemo(() => trendDirection(item.trend), [item.trend]);
  const Icon = direction === 'up' ? TrendingUpIcon : direction === 'down' ? TrendingDownIcon : TrendingFlatIcon;
  const iconColor = direction === 'up' ? color : direction === 'down' ? '#94a3b8' : '#cbd5e1';

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: idx * 0.04, duration: 0.25, ease: EASE_OUT }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 110px 24px 50px',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.85,
          borderRadius: 1.5,
          '&:hover': { bgcolor: '#f8fafc' },
          transition: 'background 0.15s',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: '#1e293b',
            fontSize: '0.82rem',
            minWidth: 0,
          }}
          noWrap
        >
          {item.name}
        </Typography>
        <Sparkline data={item.trend} color={color} />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Icon sx={{ fontSize: 16, color: iconColor }} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color,
            fontWeight: 800,
            textAlign: 'right',
            fontSize: '0.78rem',
          }}
        >
          {item.count}
        </Typography>
      </Box>
    </motion.div>
  );
};

const PollItemsTrend: React.FC<Props> = ({ positive, negative, loading }) => {
  if (loading) return <SkeletonCard />;

  const posTop = positive.slice(0, 5);
  const negTop = negative.slice(0, 5);

  if (!posTop.length && !negTop.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid rgba(120,105,223,0.10)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 2,
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 3px 10px rgba(139,92,246,0.3)',
                flexShrink: 0,
              }}
            >
              <TimelineIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
              Tendência dos últimos 12 semanas
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 2, ml: 5.75 }}>
            Evolução da frequência de seleção por semana
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {posTop.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: '#16a34a',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    fontSize: '0.68rem',
                    display: 'block',
                    mb: 0.5,
                    px: 1,
                  }}
                >
                  🟢 Elogios
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {posTop.map((item, idx) => (
                    <TrendRow key={item.pollItemId} item={item} color="#22c55e" idx={idx} />
                  ))}
                </Box>
              </Box>
            )}

            {negTop.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: '#dc2626',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    fontSize: '0.68rem',
                    display: 'block',
                    mb: 0.5,
                    px: 1,
                  }}
                >
                  🔴 Críticas
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {negTop.map((item, idx) => (
                    <TrendRow key={item.pollItemId} item={item} color="#ef4444" idx={idx} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PollItemsTrend;
